import { useQuery } from '@tanstack/react-query';
import {
  ArrowRightLeft,
  ArrowUpRight,
  Blocks,
  FolderX,
  Globe,
  Image,
  Inbox,
  Rows3,
  Upload,
} from 'lucide-react';
import type { ReactNode } from 'react';

import { RouterAnchor } from '@/app/layout/RouterAnchor';
import { kinds } from '@/app/site/scope';
import { typeIcon } from '@/app/site/typeIcon';
import { useSiteScope } from '@/app/site/useSiteScope';
import { humanSize } from '@/features/media/format';
import { siteHome } from '@/shared/api/generated/activity/activity';
import type { DivergenceBody, EventBody, RecordRefBody } from '@/shared/api/generated/model';
import { locale, t } from '@/shared/i18n';
import { plural } from '@/shared/lib/plural';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Skeleton } from '@/shared/ui/Skeleton';

import styles from './SiteHomePage.module.scss';

/**
 * Главная сайта — «что от меня нужно» (docs/research/07): что ждёт
 * человека, что он недавно правил, что делала команда, как прошла неделя и
 * что на деве ещё не перенесено на прод. Ниже — коллекции: на телефоне
 * колонки нет, и открываются они отсюда.
 */
export function SiteHomePage() {
  const scope = useSiteScope();

  const home = useQuery({
    queryKey: ['home', scope?.siteId, scope?.env],
    enabled: scope?.environment !== undefined,
    queryFn: async () => {
      const response = await siteHome(scope?.siteId ?? '', kinds[scope?.env ?? 'prod']);

      if (response.status !== 200) {
        throw new Error('Неожиданный ответ на запрос главной');
      }

      return response.data;
    },
  });

  if (scope === null) {
    return null;
  }

  const base = `/sites/${scope.siteId}/${scope.env}`;
  const shaper = scope.can('site.shape');
  const writer = scope.can('content.write');

  if (!scope.loading && scope.environment === undefined) {
    return (
      <EmptyState
        icon={<FolderX />}
        title={t.env.missingDevelopment}
        action={
          <Button variant="outline" tone="neutral" asChild>
            <RouterAnchor href={`/sites/${scope.siteId}/prod`}>
              <Globe size={15} />
              {t.env.production}
            </RouterAnchor>
          </Button>
        }
      />
    );
  }

  const data = home.data;
  const collections = scope.types.filter((type) => type.kind !== 'block');

  return (
    <div className={styles['page']}>
      <header className={styles['head']}>
        <h1 className={styles['title']}>
          {scope.site?.name ?? <Skeleton width={220} height={30} />}
        </h1>
      </header>

      {data === undefined ? (
        <div className={styles['stats']}>
          <Skeleton height={72} />
          <Skeleton height={72} />
          <Skeleton height={72} />
          <Skeleton height={72} />
        </div>
      ) : (
        <section className={styles['stats']} aria-label="Последние 7 дней">
          <Stat
            value={data.week.created}
            label={`${plural(data.week.created, ['новая запись', 'новые записи', 'новых записей'])} за неделю`}
          />
          <Stat
            value={data.week.edited}
            label={plural(data.week.edited, [
              'запись правили',
              'записи правили',
              'записей правили',
            ])}
          />
          <Stat
            value={data.week.published}
            label={plural(data.week.published, ['публикация', 'публикации', 'публикаций'])}
          />
          <Stat
            value={data.totals.live}
            label={`на сайте из ${String(data.totals.records)} ${plural(data.totals.records, ['записи', 'записей', 'записей'])}`}
          />
        </section>
      )}

      <div className={styles['columns']}>
        <div className={styles['column']}>
          <Panel title="Ждёт вас" icon={<Inbox size={16} />}>
            {data === undefined ? (
              <Skeleton height={80} />
            ) : data.waiting.total === 0 ? (
              <p className={styles['muted']}>
                Всё ваше — на сайте. Черновики и неопубликованные правки появятся здесь.
              </p>
            ) : (
              <>
                <RecordList base={base} records={data.waiting.records ?? []} />
                {data.waiting.total > (data.waiting.records ?? []).length && (
                  <p className={styles['muted']}>
                    И ещё {data.waiting.total - (data.waiting.records ?? []).length}.
                  </p>
                )}
              </>
            )}
          </Panel>

          {data?.divergence !== undefined && (
            <Divergence
              divergence={data.divergence}
              siteID={scope.siteId}
              env={scope.env}
              shaper={shaper}
            />
          )}

          <Panel title="Вы недавно правили">
            {data === undefined ? (
              <Skeleton height={80} />
            ) : (data.recent ?? []).length === 0 ? (
              <p className={styles['muted']}>Здесь будут записи, которые вы правили последними.</p>
            ) : (
              <RecordList base={base} records={data.recent ?? []} />
            )}
          </Panel>
        </div>

        <div className={styles['column']}>
          <Panel title="Быстрые действия">
            <div className={styles['actions']}>
              {writer &&
                collections
                  .filter((type) => type.purpose === 'content')
                  .slice(0, 3)
                  .map((type) => (
                    <Button key={type.id} asChild size="sm" variant="outline" tone="neutral">
                      <RouterAnchor
                        href={`${base}/${type.purpose === 'form' ? 'forms' : 'models'}/${type.id}`}
                      >
                        {typeIcon(type)}
                        {type.name}
                      </RouterAnchor>
                    </Button>
                  ))}
              {writer && (
                <Button asChild size="sm" variant="outline" tone="neutral">
                  <RouterAnchor href={`${base}/media`}>
                    <Upload size={15} />
                    Загрузить файлы
                  </RouterAnchor>
                </Button>
              )}
              {shaper && (
                <Button asChild size="sm" variant="outline" tone="neutral">
                  <RouterAnchor href={`${base}/models`}>
                    <Blocks size={15} />
                    {t.nav.model}
                  </RouterAnchor>
                </Button>
              )}
            </div>
          </Panel>

          <Panel title="Что делала команда">
            {data === undefined ? (
              <Skeleton height={120} />
            ) : (data.feed ?? []).length === 0 ? (
              <p className={styles['muted']}>
                Правки коллег в этом окружении будут здесь — кто, что и когда.
              </p>
            ) : (
              <ol className={styles['feed']}>
                {(data.feed ?? []).map((event) => (
                  <FeedRow key={event.id} event={event} />
                ))}
              </ol>
            )}
          </Panel>
        </div>
      </div>

      <section className={styles['collections']} aria-label={t.nav.content}>
        <h2 className={styles['section-title']}>Коллекции</h2>

        {scope.loading ? (
          <div className={styles['grid']}>
            <Skeleton height={96} />
            <Skeleton height={96} />
            <Skeleton height={96} />
          </div>
        ) : collections.length === 0 ? (
          <EmptyState
            icon={<Rows3 />}
            title={t.siteHome.emptyTitle}
            description={shaper ? t.siteHome.emptyShaper : t.siteHome.emptyEditor}
            action={
              shaper ? (
                <Button variant="outline" tone="neutral" asChild>
                  <RouterAnchor href={`${base}/models`}>
                    <Blocks size={15} />
                    {t.siteHome.openModel}
                  </RouterAnchor>
                </Button>
              ) : undefined
            }
          />
        ) : (
          <div className={styles['grid']}>
            {collections.map((type) => (
              <RouterAnchor
                key={type.id}
                href={
                  type.purpose === 'users'
                    ? `${base}/users`
                    : `${base}/${type.purpose === 'form' ? 'forms' : 'models'}/${type.id}`
                }
                className={styles['card']}
              >
                <span className={styles['card__icon']} aria-hidden="true">
                  {typeIcon(type)}
                </span>
                <span className={styles['card__body']}>
                  <span className={styles['card__name']}>{type.name}</span>
                  <span className={styles['card__kind']}>
                    {type.kind === 'single' ? t.siteHome.single : t.siteHome.collection}
                  </span>
                  {type.description !== '' && (
                    <span className={styles['card__text']}>{type.description}</span>
                  )}
                </span>
              </RouterAnchor>
            ))}

            <RouterAnchor href={`${base}/media`} className={styles['card']}>
              <span className={styles['card__icon']} aria-hidden="true">
                <Image />
              </span>
              <span className={styles['card__body']}>
                <span className={styles['card__name']}>{t.nav.media}</span>
                <span className={styles['card__text']}>
                  {data === undefined
                    ? t.siteHome.mediaHint
                    : `${t.siteHome.mediaHint} · ${String(data.totals.media)} · ${humanSize(data.totals.media_bytes)}`}
                </span>
              </span>
            </RouterAnchor>
          </div>
        )}
      </section>
    </div>
  );
}

function Stat({ value, label }: { value: number; label: string }) {
  return (
    <div className={styles['stat']}>
      <span className={styles['stat__value']}>{value}</span>
      <span className={styles['stat__label']}>{label}</span>
    </div>
  );
}

function Panel({
  title,
  icon,
  children,
}: {
  title: string;
  icon?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className={styles['panel']}>
      <h2 className={styles['panel__title']}>
        {icon}
        {title}
      </h2>
      {children}
    </section>
  );
}

function RecordList({ base, records }: { base: string; records: RecordRefBody[] }) {
  return (
    <ul className={styles['records']}>
      {records.map((record) => (
        <li key={record.id}>
          <RouterAnchor href={`${base}/r/${record.id}`} className={styles['record']}>
            <span className={styles['record__title']}>
              {record.title === '' ? 'Без названия' : record.title}
            </span>
            <span className={styles['muted']}>{record.type_name}</span>
            <Badge size="sm" tone={record.published ? 'warning' : 'neutral'}>
              {record.published ? (record.diverged ? 'есть правки' : 'на сайте') : 'черновик'}
            </Badge>
          </RouterAnchor>
        </li>
      ))}
    </ul>
  );
}

/**
 * Что на деве не перенесено на прод. На деве — со ссылками на сами записи
 * (там их и переносят), на проде — со ссылкой на дев.
 */
function Divergence({
  divergence,
  siteID,
  env,
  shaper,
}: {
  divergence: DivergenceBody;
  siteID: string;
  env: 'prod' | 'dev';
  shaper: boolean;
}) {
  const total = divergence.schema + divergence.new + divergence.changed;
  const devBase = `/sites/${siteID}/dev`;

  if (total === 0) {
    return (
      <Panel title="Development и Production" icon={<ArrowRightLeft size={16} />}>
        <p className={styles['muted']}>Совпадают: всё сделанное на деве уже на проде.</p>
      </Panel>
    );
  }

  const parts = [
    divergence.new > 0 ? `новых записей ${String(divergence.new)}` : '',
    divergence.changed > 0 ? `изменённых ${String(divergence.changed)}` : '',
    divergence.schema > 0 ? `изменений схемы ${String(divergence.schema)}` : '',
  ].filter((part) => part !== '');

  return (
    <Panel title="Не перенесено на Production" icon={<ArrowRightLeft size={16} />}>
      <p className={styles['muted']}>На Development: {parts.join(', ')}.</p>

      {env === 'dev' && (divergence.records ?? []).length > 0 && (
        <ul className={styles['records']}>
          {(divergence.records ?? []).map((record) => (
            <li key={record.id}>
              <RouterAnchor href={`${devBase}/r/${record.id}`} className={styles['record']}>
                <span className={styles['record__title']}>
                  {record.title === '' ? 'Без названия' : record.title}
                </span>
                <span className={styles['muted']}>{record.type_name}</span>
                <Badge size="sm" tone={record.kind === 'new' ? 'success' : 'warning'}>
                  {record.kind === 'new' ? 'новая' : 'изменена'}
                </Badge>
              </RouterAnchor>
            </li>
          ))}
        </ul>
      )}

      <div className={styles['actions']}>
        {env === 'prod' && (
          <Button asChild size="sm" variant="outline" tone="neutral">
            <RouterAnchor href={devBase}>
              <ArrowUpRight size={15} />
              Открыть Development
            </RouterAnchor>
          </Button>
        )}
        {shaper && divergence.schema > 0 && (
          <Button asChild size="sm" variant="outline" tone="neutral">
            <RouterAnchor href={`${devBase}/transfer`}>
              <ArrowRightLeft size={15} />
              Перенести схему
            </RouterAnchor>
          </Button>
        )}
      </div>
    </Panel>
  );
}

function FeedRow({ event }: { event: EventBody }) {
  const actor =
    event.actor === undefined
      ? t.activity.deletedUser
      : event.actor.name === ''
        ? event.actor.email
        : event.actor.name;

  return (
    <li className={styles['feed__item']}>
      <p className={styles['feed__line']}>
        <b>{actor}</b> — {t.activity.actions[event.action] ?? event.action}
        {event.target_label !== '' && <> «{event.target_label}»</>}
      </p>
      <time className={styles['muted']} dateTime={event.created_at}>
        {ago(event.created_at)}
      </time>
    </li>
  );
}

const relative = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });

/** «5 минут назад», «вчера» — в ленте важнее давность, чем точное время. */
function ago(value: string): string {
  const seconds = (new Date(value).getTime() - Date.now()) / 1000;
  const steps: [Intl.RelativeTimeFormatUnit, number][] = [
    ['day', 86400],
    ['hour', 3600],
    ['minute', 60],
  ];

  for (const [unit, size] of steps) {
    if (Math.abs(seconds) >= size) {
      return relative.format(Math.round(seconds / size), unit);
    }
  }

  return relative.format(0, 'minute');
}
